import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../state/store';
import {
  clearError,
  clearFilters,
  setFilters,
  clearSelectedShipment,
  clearShipmentHistory
} from '../../state/shipment/shipment.slice';
import { getAllShipments, getShipmentById, updateShipment, createShipment, getShipmentHistory } from '../../services/ShipmentService';
import { fetchAllCampaigns, fetchAllOrganizations } from '../../services/CampaignService';
import { ShipmentStatus, ShipmentFilter, ShipmentDetail, ShipmentListItem } from '../../types/shipment.types';
import { Campaign } from '../../types/campaign.types';
import { FaFilter, FaPlus } from 'react-icons/fa';

// Components
import { PageLayout } from '../../components/layout';
import { Button } from '../../components/Button';
import { Table } from '../../components/Table';
import { TableColumn, SortDirection } from '../../components/Table/Table.types';
import { SearchInput } from '../../components/Search';
import ShipmentStatusBadge from '../../components/Shipment/ShipmentStatusBadge';
import ShipmentFiltersModal from '../../components/Shipment/ShipmentFiltersModal';
import ShipmentDetailsSideNav from '../../components/Shipment/ShipmentDetailsSideNav';
import NewShipmentModal from '../../components/Shipment/NewShipmentModal';
import Pagination from '../../components/Pagination';

// Utilities
import { formatDate } from '../../utils/dateFormatter';
import { debounce } from '../../utils/debounce';
import { showSuccessToast, showErrorToast } from '../../lib/ToastUtility';

const PAGE_SIZE = 20;

/**
 * Shipments page component using the standardized Table component
 * Displays list of shipments with:
 * - Filtering capabilities
 * - Sortable columns
 * - Pagination
 */
const ShipmentsPageStandardized: React.FC = (): React.ReactElement => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    shipmentList,
    loading: isLoading,
    filters,
    selectedShipment,
    shipmentHistory,
    historyLoading
  } = useSelector((state: RootState) => state.shipment);

  // Local state
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showShipmentDetails, setShowShipmentDetails] = useState(false);
  const [showNewShipmentModal, setShowNewShipmentModal] = useState(false);
  const [selectedShipmentId, setSelectedShipmentId] = useState<number | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  // String array for components that expect string[]
  const [organizationStrings, setOrganizationStrings] = useState<string[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [resetPagination, setResetPagination] = useState(false);

  // Add this new state for immediate UI updates
  const [inputValue, setInputValue] = useState<string>("");
  const debouncedSearch = useMemo(
    () => debounce((value: string) => {
      if (value !== filters.search) {
        dispatch(setFilters({ ...filters, search: value }));
      }
    }, 400),
    [dispatch, filters]
  );

  // Handle immediate input changes for responsiveness
  const handleInputChange = (value: string) => {
    // Update UI immediately
    setInputValue(value);
    // Pass to debounced search for filtering
    debouncedSearch(value);
  };

  // Extract unique campaigns and organizations from shipment list
  useEffect(() => {
    const tempUniqueCampaigns = new Map<number, string>();
    const tempOrganizationStrings: string[] = [];

    shipmentList.forEach(shipment => {
      if (shipment.campaignId && shipment.campaignName) {
        tempUniqueCampaigns.set(shipment.campaignId, shipment.campaignName);
      }
    });

    const campaignList = Array.from(tempUniqueCampaigns.entries()).map(([id, name]) => ({
      id,
      name
    }));
    setCampaigns(campaignList as Campaign[]);

    shipmentList.forEach(shipment => {
      if (shipment.organization && !tempOrganizationStrings.includes(shipment.organization)) {
        tempOrganizationStrings.push(shipment.organization);
      }
    });

    setOrganizationStrings(tempOrganizationStrings);
  }, [shipmentList]);

  useEffect(() => {
    setInputValue(filters.search || '');
  }, [filters.search]);

  // Fetch active campaigns for the dropdown
  useEffect(() => {
    const fetchActiveCampaigns = async () => {
      try {
        // Pass a string as expected by the function
        dispatch(fetchAllCampaigns('1') as any)
          .then((response: any) => {
            if (response.payload && response.payload.data) {
              const campaignList = response.payload.data.campaigns.map((c: Campaign) => ({
                id: c.id,
                name: c.name
              }));
              setCampaigns(prevCampaigns => {
                // Merge unique campaigns
                const uniqueCampaigns = new Map();
                [...prevCampaigns, ...campaignList].forEach(c => {
                  uniqueCampaigns.set(c.id, c);
                });
                return Array.from(uniqueCampaigns.values());
              });
            }
          });
      } catch (error) {
        console.error('Error fetching campaigns:', error);
      }
    };

    fetchActiveCampaigns();
  }, [dispatch]);

  // Fetch organizations for the dropdown - only load once at component mount
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        // This function doesn't expect parameters
        dispatch(fetchAllOrganizations() as any)
          .then((response: any) => {
            if (response.payload && response.payload.organizations) {
              const incomingOrgNames = response.payload.organizations
                .map((org: { name?: string }) => org?.name)
                .filter((name: string | undefined): name is string => Boolean(name));

              setOrganizationStrings(prev => Array.from(new Set([...prev, ...incomingOrgNames])));
            }
          });
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };

    // Only load once when component mounts
    loadOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove dispatch dependency to ensure it only runs once

  // Fetch shipments when filters change
  useEffect(() => {
    dispatch(clearError());
    dispatch(getAllShipments(filters) as any);
  }, [dispatch, filters]);

  // Filter and sort shipments
  const filteredShipments = useMemo(() => {
    let result = [...shipmentList];

    // Apply filters
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      result = result.filter(item => {
        return (
          item.name?.toLowerCase().includes(searchTerm) ||
          item.campaignName?.toLowerCase().includes(searchTerm) ||
          item.organization?.toLowerCase().includes(searchTerm) ||
          item.status?.toLowerCase().includes(searchTerm)
        );
      });
    }

    if (filters.campaign) {
      result = result.filter(item => item.campaignId === filters.campaign);
    }

    if (filters.organization) {
      result = result.filter(item => item.organization === filters.organization);
    }

    if (filters.status) {
      // Handle array of status values
      if (Array.isArray(filters.status) && filters.status.length > 0) {
        result = result.filter(item => filters.status!.includes(item.status));
      }
    }

    if (filters.dateFrom) {
      const startDate = new Date(filters.dateFrom);
      result = result.filter(item => {
        const createdAt = new Date(item.createdAt);
        return createdAt >= startDate;
      });
    }

    if (filters.dateTo) {
      const endDate = new Date(filters.dateTo);
      result = result.filter(item => {
        const createdAt = new Date(item.createdAt);
        return createdAt <= endDate;
      });
    }

    // Note: minDonatedBars and maxDonatedBars are not in the ShipmentFilter interface
    // If you want to filter by these, you'll need to add them to the interface

    // Apply sorting
    if (filters.sortBy) {
      result.sort((a, b) => {
        let aValue: any = a[filters.sortBy as keyof ShipmentListItem];
        let bValue: any = b[filters.sortBy as keyof ShipmentListItem];

        switch (filters.sortBy) {
          case 'createdAt':
            aValue = new Date(aValue || 0).getTime();
            bValue = new Date(bValue || 0).getTime();
            break;
          case 'status':
          case 'name':
          case 'campaignName':
            aValue = (aValue || '').toString().toLowerCase();
            bValue = (bValue || '').toString().toLowerCase();
            break;
          default:
            // For numeric fields
            aValue = aValue || 0;
            bValue = bValue || 0;
        }

        if (filters.sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }

    return result;
  }, [shipmentList, filters]);

  const paginatedShipments = useMemo(
    () => filteredShipments.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredShipments, currentPage]
  );

  // Reset pagination when filters/search change
  useEffect(() => {
    setCurrentPage(1);
    setResetPagination((v) => !v);
  }, [filteredShipments]);

  // Count active filters
  const getActiveFilterCount = (): number => {
    let count = 0;
    if (filters.search) count++;
    if (filters.campaign) count++;
    if (filters.organization) count++;
    if (filters.status && filters.status.length > 0) count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    return count;
  };

  // Handle filter changes
  const handleApplyFilters = (newFilters: ShipmentFilter) => {
    dispatch(clearFilters());
    dispatch(setFilters(newFilters));
  };

  // Handle column sort
  const handleSortChange = (column: string, direction: SortDirection) => {
    dispatch(setFilters({
      ...filters,
      sortBy: column as 'createdAt' | 'status' | 'name' | 'campaignName',
      sortOrder: direction
    }));
  };

  // Handle shipment click to show details
  const handleShipmentClick = (shipmentId: number) => {
    setSelectedShipmentId(shipmentId);
    setShowShipmentDetails(true);

    dispatch(clearShipmentHistory());

    dispatch(getShipmentById(shipmentId) as any)
      .then((detailedShipment: any) => {
        // Success is handled by the Redux slice
      })
      .catch((error: any) => {
        showErrorToast(error.message || 'Failed to load shipment details');
      });

    dispatch(getShipmentHistory(shipmentId) as any);
  };

  // Handle shipment update
  const handleUpdateShipment = (updatedShipment: Partial<ShipmentDetail>) => {
    if (!selectedShipmentId) {
      showErrorToast('No shipment selected');
      return;
    }

    const payload = {
      id: selectedShipmentId,
      ...updatedShipment
    };

    dispatch(updateShipment(payload) as any)
      .then((updatedShipmentData: any) => {
        showSuccessToast('Shipment updated successfully');

        // Refresh the list to show updated data
        dispatch(getAllShipments(filters) as any);

        // Close the detail view
        setShowShipmentDetails(false);
        setSelectedShipmentId(null);
        dispatch(clearSelectedShipment());
      })
      .catch((error: any) => {
        showErrorToast(error.message || 'Failed to update shipment');
      });
  };

  // Handle new shipment
  const handleCreateShipment = (newShipment: Partial<ShipmentDetail>) => {
    dispatch(createShipment(newShipment) as any)
      .then((createdShipment: any) => {
        showSuccessToast('Shipment created successfully');

        // Refresh shipment list
        dispatch(getAllShipments(filters) as any);

        // Close the modal
        setShowNewShipmentModal(false);
      })
      .catch((error: any) => {
        showErrorToast(error.message || 'Failed to create shipment');
      });
  };

  // Define table columns
  const getColumns = (): TableColumn<ShipmentListItem>[] => [
    {
      id: 'createdAt',
      label: 'Created At',
      sortable: true,
      priority: 'high',
      format: (value) => formatDate(value)
    },
    {
      id: 'name',
      label: 'Shipment Name',
      sortable: true,
      priority: 'high',
    },
    {
      id: 'campaignName',
      label: 'Campaign',
      sortable: true,
      priority: 'medium',
    },
    {
      id: 'organization',
      label: 'Organization',
      priority: 'medium',
    },
    {
      id: 'donatedBars',
      label: 'Donated Bars',
      priority: 'low',
    },
    {
      id: 'orderedBoxes',
      label: 'Ordered Boxes',
      priority: 'low',
    },
    {
      id: 'fulfilledBoxes',
      label: 'Fulfilled Boxes',
      priority: 'low',
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      priority: 'high',
      format: (value) => <ShipmentStatusBadge status={value as ShipmentStatus} />
    }
  ];

  // Render action buttons
  const renderActions = () => (
    <div className="flex gap-2">
      <Button
        variant="secondary"
        size="sm"
        startIcon={<FaFilter />}
        onClick={() => setShowFiltersModal(true)}
      >
        Filters {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
      </Button>
      <Button
        variant="primary"
        size="sm"
        startIcon={<FaPlus />}
        onClick={() => setShowNewShipmentModal(true)}
      >
        New Shipment
      </Button>
    </div>
  );

  // Render search box
  const renderSearchBox = () => (
    <SearchInput
      value={inputValue}
      placeholder="Search shipments..."
      onChange={handleInputChange}
      showClearButton={true}
      debounce={0} // Disable component's internal debouncing
    />
  );

  // Render pagination
  const renderPagination = () =>
    filteredShipments.length > PAGE_SIZE ? (
      <Pagination
        totalRecords={filteredShipments.length}
        perPageCount={PAGE_SIZE}
        reset={resetPagination}
        setPerPageDataSet={(start, _end) => {
          const nextPage = Math.max(1, Math.ceil(start / PAGE_SIZE));
          setCurrentPage(nextPage);
        }}
      />
    ) : null;

  return (
    <>
      <PageLayout
        title="Shipments"
        actions={renderActions()}
        filters={renderSearchBox()}
        footer={renderPagination()}
      >
        <div className="overflow-x-auto">
          <Table
            data={paginatedShipments}
            columns={getColumns()}
            sortBy={filters.sortBy}
            sortDirection={filters.sortOrder}
            onSortChange={handleSortChange}
            onRowClick={(shipment) => handleShipmentClick(shipment.id)}
            hoverable={true}
            fixedHeader={true}
            loading={isLoading}
            emptyMessage={getActiveFilterCount() > 0
              ? 'No shipments found. Try adjusting your filters.'
              : 'No shipments found. Create your first shipment.'}
          />
        </div>
      </PageLayout>

      <ShipmentFiltersModal
        open={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
        initialFilters={filters}
        onApply={handleApplyFilters}
        campaigns={campaigns}
        organizations={organizationStrings}
      />

      <ShipmentDetailsSideNav
        isOpen={showShipmentDetails}
        onClose={() => {
          setShowShipmentDetails(false);
          dispatch(clearSelectedShipment());
          dispatch(clearShipmentHistory());
        }}
        onEdit={handleUpdateShipment}
        shipmentDetails={selectedShipment}
        campaigns={campaigns}
        shipmentHistory={shipmentHistory}
        historyLoading={historyLoading}
      />

      <NewShipmentModal
        open={showNewShipmentModal}
        onClose={() => setShowNewShipmentModal(false)}
        onConfirm={handleCreateShipment}
        campaigns={campaigns}
        organizations={organizationStrings}
        loadingCampaigns={false}
        loadingOrganizations={false}
      />
    </>
  );
};

export default ShipmentsPageStandardized;
